import React, { useState } from "react";
import { ActivityIndicator, Modal, Pressable, StyleSheet, Text, TextInput, View, useWindowDimensions } from "react-native";
import { useUser } from "../UserContext";
import { REFERRAL_DETAIL_MAX_LENGTH, REFERRAL_OTHER_VALUE, REFERRAL_SOURCES } from "../../constants/ReferralSources";
import { saveReferralAnswer } from "../../services/ReferralService";

const COLORS = { cream: "#fefde2", green: "#4c956c", greenDark: "#2c6e49", greenLight: "#d7ebdf", orange: "#d68c45", text: "#34423a", error: "#c0392b" };
const FONT_FAMILY = "Bodoni72Smallcaps";
const TWO_COLUMN_MIN_WIDTH = 520;

const ReferralOption = ({ label, isSelected, onPress, isFullWidth }) => (
	<Pressable
		accessibilityRole="radio"
		accessibilityState={{ checked: isSelected }}
		onPress={onPress}
		style={({ hovered }) => [styles.option, isFullWidth && styles.optionFullWidth, isSelected && styles.optionSelected, hovered && !isSelected && styles.optionHovered]}
	>
		<View style={[styles.radio, isSelected && styles.radioSelected]}>{isSelected && <View style={styles.radioDot} />}</View>
		<Text style={[styles.optionLabel, isSelected && styles.optionLabelSelected]}>{label}</Text>
	</Pressable>
);

/**
 * Shown once to brand-new users (users.referralPending === true) before anything else.
 * Saves the answer for reporting; the Spotlight Tutorial provider then launches the tutorial on My Profile.
 */
const ReferralSurveyGate = () => {
	const { currentUser, currentUserData, setCurrentUserData } = useUser();
	const { width } = useWindowDimensions();

	const [selectedSource, setSelectedSource] = useState(null);
	const [otherDetail, setOtherDetail] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const isVisible = Boolean(currentUser && currentUserData?.referralPending === true);
	const isOtherSelected = selectedSource === REFERRAL_OTHER_VALUE;
	const canSubmit = Boolean(selectedSource) && (!isOtherSelected || otherDetail.trim().length > 0) && !isSaving;
	const useTwoColumns = width >= TWO_COLUMN_MIN_WIDTH;

	const handleSubmit = async () => {
		if (!canSubmit) return;
		setIsSaving(true);
		setErrorMessage("");
		try {
			const savedFields = await saveReferralAnswer(currentUserData.email, { source: selectedSource, detail: otherDetail });
			setCurrentUserData((previous) => (previous ? { ...previous, ...savedFields } : previous));
		} catch (error) {
			console.error("Failed to save referral answer:", error);
			setErrorMessage("We couldn't save your answer. Please check your connection and try again.");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal visible={isVisible} transparent animationType="fade" onRequestClose={() => {}}>
			<View style={styles.backdrop}>
				<View style={[styles.card, { width: Math.min(560, width - 32) }]}>
					<Text style={styles.eyebrow}>Welcome to MyTassel</Text>
					<Text style={styles.title}>How did you hear about us?</Text>
					<Text style={styles.subtitle}>One quick question before we get you set up.</Text>

					<View style={styles.options} accessibilityRole="radiogroup">
						{REFERRAL_SOURCES.map((source) => (
							<ReferralOption
								key={source.value}
								label={source.label}
								isSelected={selectedSource === source.value}
								isFullWidth={!useTwoColumns}
								onPress={() => setSelectedSource(source.value)}
							/>
						))}
					</View>

					{isOtherSelected && (
						<View style={styles.otherContainer}>
							<Text style={styles.otherLabel}>Please specify</Text>
							<TextInput
								style={styles.otherInput}
								value={otherDetail}
								onChangeText={setOtherDetail}
								onSubmitEditing={handleSubmit}
								placeholder="Where did you hear about MyTassel?"
								placeholderTextColor="#8aa394"
								maxLength={REFERRAL_DETAIL_MAX_LENGTH}
								autoFocus
								returnKeyType="done"
							/>
						</View>
					)}

					{Boolean(errorMessage) && <Text style={styles.error}>{errorMessage}</Text>}

					<Pressable onPress={handleSubmit} disabled={!canSubmit} style={[styles.submitButton, !canSubmit && styles.submitButtonDisabled]}>
						{isSaving ? <ActivityIndicator color="#ffffff" /> : <Text style={styles.submitLabel}>Continue</Text>}
					</Pressable>
				</View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	backdrop: { flex: 1, backgroundColor: "rgba(18, 36, 27, 0.78)", alignItems: "center", justifyContent: "center", padding: 16 },
	card: { backgroundColor: COLORS.cream, borderRadius: 24, padding: 28, shadowColor: "#000", shadowOpacity: 0.35, shadowRadius: 30, shadowOffset: { width: 0, height: 16 } },
	eyebrow: { fontFamily: FONT_FAMILY, fontSize: 13, letterSpacing: 1, textTransform: "uppercase", color: COLORS.green, fontWeight: "700", textAlign: "center" },
	title: { fontFamily: FONT_FAMILY, fontSize: 28, color: COLORS.greenDark, fontWeight: "700", textAlign: "center", marginTop: 6 },
	subtitle: { fontFamily: FONT_FAMILY, fontSize: 16, color: COLORS.text, textAlign: "center", marginTop: 6, marginBottom: 20 },
	options: { flexDirection: "row", flexWrap: "wrap", justifyContent: "space-between", rowGap: 10 },
	option: { width: "48.5%", flexDirection: "row", alignItems: "center", gap: 10, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 14, borderWidth: 1.5, borderColor: COLORS.greenLight, backgroundColor: "#ffffff" },
	optionFullWidth: { width: "100%" },
	optionHovered: { borderColor: COLORS.green },
	optionSelected: { borderColor: COLORS.green, backgroundColor: COLORS.greenLight },
	radio: { width: 20, height: 20, borderRadius: 10, borderWidth: 2, borderColor: COLORS.green, alignItems: "center", justifyContent: "center" },
	radioSelected: { backgroundColor: COLORS.green },
	radioDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: "#ffffff" },
	optionLabel: { fontFamily: FONT_FAMILY, fontSize: 16, color: COLORS.text, flexShrink: 1 },
	optionLabelSelected: { color: COLORS.greenDark, fontWeight: "700" },
	otherContainer: { marginTop: 16 },
	otherLabel: { fontFamily: FONT_FAMILY, fontSize: 14, color: COLORS.greenDark, fontWeight: "700", marginBottom: 6 },
	otherInput: { fontFamily: FONT_FAMILY, fontSize: 16, color: COLORS.text, backgroundColor: "#ffffff", borderWidth: 1.5, borderColor: COLORS.green, borderRadius: 14, paddingVertical: 12, paddingHorizontal: 14, outlineStyle: "none" },
	error: { fontFamily: FONT_FAMILY, fontSize: 14, color: COLORS.error, textAlign: "center", marginTop: 14 },
	submitButton: { marginTop: 22, backgroundColor: COLORS.green, borderRadius: 999, paddingVertical: 14, alignItems: "center", justifyContent: "center" },
	submitButtonDisabled: { backgroundColor: "#b9c9bf" },
	submitLabel: { fontFamily: FONT_FAMILY, fontSize: 17, color: "#ffffff", fontWeight: "700" },
});

export default ReferralSurveyGate;
